import {
  Autocomplete,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
} from '@mui/material';
import { RiFilterFill, RiFilterLine } from '@remixicon/react';
import { useMemo, useState } from 'react';
import { useCurrentUser } from '../../contexts/currentUser';
import { useLanguage } from '../../contexts/language';
import { EntityStatus, JourneyStatus, SelectItem } from '../../types/core';
import { ContractListItem } from '../../types/contract';

export interface FilterProps {
  className?: string;
  contracts: ContractListItem[];
  isFetchingContracts: boolean;
  selectedContractId: string | null;
  onSelectedContractIdChange: (newSelectedContractId: string | null) => void;
  isLoadingJourneyPlaceholders: boolean;
  journeyStatusFilters: JourneyStatus[];
  onJourneyStatusFiltersChange: (newFilters: JourneyStatus[]) => void;
  entityStatusFilters: EntityStatus[];
  onEntityStatusFiltersChange: (newFilters: EntityStatus[]) => void;
}

const Filters = ({
  className,
  contracts,
  isFetchingContracts,
  selectedContractId,
  onSelectedContractIdChange,
  journeyStatusFilters,
  onJourneyStatusFiltersChange,
  entityStatusFilters,
  onEntityStatusFiltersChange,
}: FilterProps) => {
  const { t } = useLanguage();
  const { userOperator } = useCurrentUser();

  const isOperator = Boolean(userOperator);

  const [mobileOpen, setMobileOpen] = useState(false);

  const { contractItems, contractItemsMap } = useMemo(() => {
    const contractItems: SelectItem[] = [];
    const contractItemsMap: Record<string, SelectItem> = {};
    contracts.forEach(contract => {
      const item: SelectItem = {
        value: contract.id,
        label: `${!isOperator ? contract.operatorName + ' - ' : ''}${contract.name}`,
      };
      contractItemsMap[item.value] = item;
      contractItems.push(item);
    });
    contractItems.sort((a, b) => a.label.localeCompare(b.label));

    return { contractItems, contractItemsMap };
  }, [contracts, isOperator]);

  return (
    <div
      className={[
        'inline-grid grid-cols-[1fr_min-content] items-start gap-2',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="grid grid-cols-[1fr] grid-rows-[repeat(3,auto)] md:grid-cols-[minmax(200px,1fr)_minmax(100px,200px)_minmax(100px,200px)] md:grid-rows-[auto] items-center gap-2 md:gap-4">
        <Autocomplete<SelectItem>
          value={contractItemsMap[selectedContractId ?? ''] ?? null}
          disabled={isFetchingContracts}
          options={contractItems}
          renderInput={params => (
            <TextField {...params} placeholder={t('content.contract')} />
          )}
          onChange={(_, item) =>
            onSelectedContractIdChange(item?.value ?? null)
          }
          getOptionLabel={contract => contract.label}
        />
        <Select
          value={journeyStatusFilters}
          multiple
          disabled={isFetchingContracts}
          className={`overflow-hidden ${mobileOpen ? '' : 'hidden md:flex'}`}
          displayEmpty
          renderValue={value =>
            value.length === 0 ? (
              <span className="opacity-[0.42]">
                {t('content.journey-status')}
              </span>
            ) : (
              value.map(status => t(`journey-status.${status}`)).join(', ')
            )
          }
          onChange={event => {
            onJourneyStatusFiltersChange(event.target.value as JourneyStatus[]);
          }}
        >
          {[
            JourneyStatus.AsScheduled,
            JourneyStatus.Rescheduled,
            JourneyStatus.ReliefEvent,
            JourneyStatus.Cancelled,
          ].map(status => (
            <MenuItem key={status} value={status}>
              <Checkbox checked={journeyStatusFilters.includes(status)} />
              <ListItemText
                primary={`${status === JourneyStatus.AsScheduled ? `${t('content.on-time')} / ` : ''}${t(`journey-status.${status}`)}`}
              />
            </MenuItem>
          ))}
        </Select>
        <Select
          value={entityStatusFilters}
          multiple
          disabled={isFetchingContracts}
          className={`${mobileOpen ? '' : 'hidden md:flex'}`}
          displayEmpty
          renderValue={value =>
            value.length === 0 ? (
              <span className="opacity-[0.42]">{t('content.log-status')}</span>
            ) : (
              value
                .map(status => t(`journey-entity-status.${status}`))
                .join(', ')
            )
          }
          onChange={event => {
            onEntityStatusFiltersChange(event.target.value as EntityStatus[]);
          }}
        >
          {[
            EntityStatus.NotLogged,
            EntityStatus.Logged,
            EntityStatus.PendingApproval,
            EntityStatus.Rejected,
            EntityStatus.Submitted,
            EntityStatus.Invoiced,
          ].map(status => (
            <MenuItem key={status} value={status}>
              <Checkbox checked={entityStatusFilters.includes(status)} />
              <ListItemText primary={t(`journey-entity-status.${status}`)} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <IconButton
        className="md:hidden"
        size="small"
        onClick={() => setMobileOpen(o => !o)}
        aria-label="Toggle filters"
      >
        <SvgIcon component={mobileOpen ? RiFilterFill : RiFilterLine} />
      </IconButton>
    </div>
  );
};

export default Filters;
